import { courseHasReadingAid } from "../reading-aids.js";
import WordList from "./word-list.marko";
import Blocks from "./blocks.marko";
import StoryLines from "./story-lines.marko";
import StoryEnding from "./story-ending.marko";
import Quiz from "./quiz.marko";
import LessonBar from "./lesson-bar.marko";
import NextCard from "./next-card.marko";
import UnitLinks from "./unit-links.marko";
import Switch from "./switch.marko";
import Icon from "./icon.marko";
import PdfLinks from "./pdf-links.marko";
import Paywall from "./paywall.marko";
import Picture from "./picture.marko";
import Play from "./play.marko";
import GrammarTeaching from './grammar-teaching.marko';
import ModuleDone from "./module-done.marko";

static const UNIT_ICONS = { words: "Aa", grammar: "§", "vocab-review": "Aa", "grammar-review": "§", story: "✦" };

<const/u=input.unit/>
<const/copy=input.copy/>
<const/uiLang=input.uiLang || $global.uiLang || "en"/>
<const/labels={
  intro: copy.overview,
  story: copy.theStory,
  learn: copy.overview,
  ...Object.fromEntries(u.parts.map((part) => [part.id, part.title])),
  ...Object.fromEntries((u.teachingStages || []).map((stage, index, stages) => {
    const phase = stage.exercises ? copy.practice : copy.overview;
    const topic = index === stages.length - 1 ? (copy.all || "All") : stage.title;
    return [stage.id, `${topic} · ${phase}`];
  })),
  done: copy.done,
}/>
<const/premium=u.access === "premium"/>
<const/doneArt=u.story?.art || input.scene?.art/>
<const/practiceLabel=(u.type === "words" ? copy.words : u.type === "grammar" ? copy.units.grammar : "")/>
<article
  class=["ll-course-lesson", "is-stepped", "ll-course-unit", `ll-course-unit--${u.type}`]
  data-ll-lesson
  data-ll-unit=u.id
  data-ll-id=u.id
  data-ll-progress-version=(u.progressVersion || false)
  data-ll-grammar-teaching=(u.teachingStages ? '1' : false)
  data-ll-access=u.access
  data-ll-steps=input.steps.join(" ")
  data-ll-labels=JSON.stringify(labels)
  data-ll-course-home=input.nav.course
>
  <LessonBar back=input.nav.course steps=input.steps labels=labels title=u.title presets=input.presets copy=copy lang=input.lang sceneNumber=(practiceLabel && input.scene?.n) kindLabel=practiceLabel/>

  <div class="ll-course-lesson__page">
    <div class=["ll-course-gate", premium && "is-locked"] data-ll-gate=u.access data-ll-first-module-practice=(u.firstModulePractice ? "1" : false)>
      <if=premium>
      <aside class="ll-course-lock-note" data-ll-paywall><h2>${copy.premiumTitle}</h2><button class="ll-course-primary ll-course-primary--premium" type="button" data-ll-premium-request>${copy.premiumCta}</button></aside>
      </if>
      <div class="ll-course-gate__content" data-ll-gate-content inert=premium>
        <if=u.teachingStages>
          <GrammarTeaching unit=u copy=copy lang=input.lang/>
        </if>
        <else-if=u.type === "story">
          <section class="ll-course-step ll-course-intro" data-ll-step="intro">
            <if=u.story.art>
              <figure class="ll-course-art">
                <Picture image=u.story.art priority="high"/>
              </figure>
            </if>
            <p class="ll-course-kicker ll-course-kicker--warm">
              ${input.label}
              <if=premium>
                <span class="ll-course-badge">${copy.premiumBadge}</span>
              </if>
            </p>
            <h1 class="ll-course-scene-title" lang=input.lang dir="auto">${u.title}</h1>
            <if=u.titleEn && u.titleEn !== u.title>
              <p class="ll-course-sub ll-course-gloss">${u.titleEn}</p>
            </if>
            <if=input.module>
              <a class="ll-course-unit__scene" href=input.module.href>${copy.module} ${input.module.n} · ${input.module.title}</a>
            </if>
            <p class="ll-course-note">${copy.bonusNote}</p>
            <for|v| of=u.story.situation>
              <p class="ll-course-goal" data-g=(v.g || false)>
                <span class="ll-course-goal__label">${copy.theStory}</span>
                <span>${v.text}</span>
              </p>
            </for>
            <if=u.story.glossed.length>
              <h2 class="ll-course-done__head">${copy.glossedTitle}</h2>
              <ul class="ll-course-tiles">
                <for|item| of=u.story.glossed>
                  <li class="ll-course-tile">
                    <span class="ll-course-tile__top"><strong>${item.word}</strong> <Play src=item.audio label=copy.play/></span>
                    <span class="ll-course-tile__en">${item.en}</span>
                  </li>
                </for>
              </ul>
            </if>
            <div class="ll-course-dock">
              <button type="button" class="ll-course-primary ll-course-wide" data-ll-go="story">
                ${copy.startStory} <Icon name="right"/>
              </button>
            </div>
          </section>

          <section class="ll-course-step" data-ll-step="story" hidden>
            <div class="ll-course-tools">
              <p class="ll-course-hint">${copy.lineWord}</p>
              <div class="ll-course-tools__toggles">
                <Switch label=copy.autoplay on=true attrs={ "data-ll-autoplay": "" }/>
                <if=(input.lang !== uiLang)>
                  <Switch label=copy.showEnglish on=true attrs={ "data-ll-english": "" }/>
                </if>
              </div>
            </div>
            <StoryLines blocks=u.story.blocks copy=copy lang=input.lang/>
            <div class="ll-course-dock" data-ll-actions>
              <button type="button" class="ll-course-ghost" data-ll-show-all>${copy.showAll}</button>
              <button type="button" class="ll-course-primary ll-course-grow" data-ll-next-line>
                ${copy.nextLine} <span class="ll-course-dock__count" data-ll-line-count></span>
              </button>
              <button type="button" class="ll-course-primary ll-course-wide" data-ll-go="next">
                ${u.exercises.length ? copy.storyQuestions : copy.continueOn} <Icon name="right"/>
              </button>
            </div>
          </section>
        </else-if>
        <else>
          <section class="ll-course-step" data-ll-step="learn">
            <header class="ll-course-unit__head">
              <span class=["ll-course-unit__icon", `ll-course-unit-icon--${u.type}`] aria-hidden="true">${UNIT_ICONS[u.type] || "•"}</span>
              <div class="ll-course-unit__titles">
                <p class="ll-course-kicker ll-course-kicker--warm ll-course-unit__kicker">
                  <if=input.module>
                    <a href=input.module.href title=input.module.title>${copy.module} ${input.module.n}</a>
                  </if>
                  <else>
                    <a href=input.scene.href title=input.scene.title>${copy.scene} ${input.scene.n}</a>
                  </else>
                  <span aria-hidden="true">·</span>
                  ${input.label}
                  <if=premium>
                    <span class="ll-course-badge">${copy.premiumBadge}</span>
                  </if>
                </p>
                <h1 class="ll-course-page-title">${u.title}</h1>
                <const/subtitle=u.subtitle || (u.titleEn !== u.title ? u.titleEn : "")/>
                <if=subtitle>
                  <p class=["ll-course-unit__subtitle", !u.subtitle && "ll-course-gloss"]>${subtitle}</p>
                </if>
              </div>
            </header>
            <if=u.sides>
              <p class="ll-course-note">${u.sides}</p>
            </if>
            <if=u.canDo?.length>
              <section class="ll-course-cando">
                <h2>${copy.canDoTitle}</h2>
                <ul>
                  <for|item| of=u.canDo>
                    <li><Icon name="check" size=16 weight=3/> ${item}</li>
                  </for>
                </ul>
              </section>
            </if>
            <if=u.groups>
              <WordList groups=u.groups copy=copy lang=input.lang/>
            </if>
            <if=u.blocks>
              <section class="ll-course-grammar">
                <Blocks blocks=u.blocks copy=copy lang=input.lang/>
              </section>
            </if>
            <if=u.sections>
              <for|section| of=u.sections>
                <section class="ll-course-grammar">
                  <h2>${section.title}</h2>
                  <Blocks blocks=section.blocks copy=copy lang=input.lang/>
                </section>
              </for>
            </if>
            <div class="ll-course-dock">
              <button type="button" class="ll-course-primary ll-course-wide" data-ll-go="next">
                ${u.exercises.length ? copy.practise : copy.continueOn} <Icon name="right"/>
              </button>
            </div>
          </section>
        </else>

        <for|part, i| of=(!u.teachingStages ? u.parts : [])>
          <section class="ll-course-step ll-course-part" data-ll-step=part.id hidden>
            <p class="ll-course-kicker ll-course-kicker--warm">${copy.practice} ${i + 1}/${u.parts.length} · ${part.title}</p>
            <Quiz exercises=part.exercises copy=copy gloss=(u.type === "words" && part.kind === "pictures") reading=(u.type === "words" && part.kind === "pictures" && courseHasReadingAid(input.lang)) typing=part.exercises.some(ex => ex.view === "gaps") lang=input.lang/>
          </section>
        </for>

        <section class="ll-course-step ll-course-done" data-ll-step="done" hidden>
          <figure class=["ll-course-art", "ll-course-done__art", !doneArt && "ll-course-done__art--plain", u.story?.ending && "ll-course-done__art--ending"]>
            <if=u.story?.ending>
              <StoryEnding ending=u.story.ending lang=input.lang/>
            </if>
            <else-if=doneArt>
              <Picture image=doneArt/>
            </else-if>
            <else>
              <span class=["ll-course-done__icon", `ll-course-unit-icon--${u.type}`] aria-hidden="true">${UNIT_ICONS[u.type] || "•"}</span>
            </else>
            <span class="ll-course-done__badge"><Icon name="check" size=26 weight=3/></span>
          </figure>
          <if=u.story?.ending>
            <p class="ll-course-hint ll-course-done__tap">${copy.tapSomeone}</p>
          </if>
          <if=input.moduleSummary>
            <!-- A module's last unit: celebrate the module, then straight on to the next one. -->
            <ModuleDone summary=input.moduleSummary copy=copy lang=input.lang>
              <PdfLinks pdf=input.pdf solutions=input.solutions premium=premium firstModulePractice=u.firstModulePractice copy=copy/>
            </ModuleDone>
            <div class="ll-course-done__actions">
              <NextCard item=input.around.next kicker=copy.nextModule copy=copy lang=input.lang/>
            </div>
            <div class="ll-course-done__actions">
              <a class="ll-course-ghost ll-course-wide" href=input.nav.course>${copy.back}</a>
            </div>
          </if>
          <else>
          <p class="ll-course-kicker ll-course-kicker--ok">${copy.unitComplete}</p>
          <h1 class="ll-course-scene-title" lang=input.lang dir="auto">${u.title}</h1>
          <div class="ll-course-stats">
            <if=u.exercises.length>
              <div><strong>${u.exercises.length}</strong><span>${copy.exercisesCount}</span></div>
            </if>
            <if=u.groups>
              <div><strong>${u.groups.reduce((n, g) => n + g.words.length, 0)}</strong><span>${copy.newWords}</span></div>
            </if>
            <if=u.story>
              <div><strong>${u.story.lineCount}</strong><span>${copy.linesRead}</span></div>
            </if>
            <div data-ll-score hidden><strong data-ll-score-text></strong><span>${copy.rightFirst}</span></div>
          </div>
          <if=input.units?.length>
            <h2 class="ll-course-done__head">${copy.keepPractising}</h2>
            <UnitLinks units=input.units copy=copy lang=input.lang/>
          </if>
          <PdfLinks pdf=input.pdf solutions=input.solutions premium=premium firstModulePractice=u.firstModulePractice copy=copy/>
          <div class="ll-course-done__actions">
            <if=input.units?.some(unit => unit.id === input.around.next?.id)>
              <NextCard item=input.around.nextScene kicker=copy.nextScene copy=copy lang=input.lang/>
            </if>
            <else>
              <NextCard item=input.around.next kicker=copy.upNext copy=copy lang=input.lang/>
              <if=input.around.nextScene && input.around.next && input.around.next.kind !== "scene">
                <a class="ll-course-ghost ll-course-wide" href=input.around.nextScene.href>${copy.skipToScene}</a>
              </if>
            </else>
            <a class="ll-course-ghost ll-course-wide" href=input.nav.course>${copy.back}</a>
          </div>
          </else>
        </section>
      </div>
    </div>
  </div>
</article>
